export default class ServiceMap {
    private static serviceModelMapApi: any = null;
    private static serviceMap: any = null;
    private static cacheTimestamp: number = 0;
    private static readonly CACHE_DURATION = 600000; // 10 minutes
    private static ongoingRequest: Promise<any> | null = null;
    
    // Lazy load ServiceModelMap API
    private static async getServiceModelMapApi(): Promise<any> {
        if (!this.serviceModelMapApi) {
            const { default: ServiceModelMap } = await import("@/API/ServiceModelMap/ServiceModelMap");
            this.serviceModelMapApi = new ServiceModelMap();
        }
        return this.serviceModelMapApi;
    }
    
    /**
     * Get service map with intelligent caching and request deduplication
     * Multiple simultaneous calls will share the same request to prevent API spam
     */
    static async getServiceMap(): Promise<any> {
        const now = Date.now();
        
        // Return cached data if it's still valid
        if (this.serviceMap && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
            return this.serviceMap;
        }
        
        // If there's already an ongoing request, wait for it instead of making a new one
        if (this.ongoingRequest) {
            return await this.ongoingRequest;
        }
        
        // Start new request and cache the promise to prevent duplicate requests
        this.ongoingRequest = this.fetchServiceMap();
        
        try {
            const result = await this.ongoingRequest;
            this.cacheTimestamp = now;
            this.serviceMap = result;
            return result;
        } finally {
            this.ongoingRequest = null;
        }
    }
    
    private static async fetchServiceMap(): Promise<any> {
        const api = await this.getServiceModelMapApi();
        const response = await api.getAll({ perPage: 1000 });

        // Response could be an array or an object with data property
        let records: any[] = [];
        if (Array.isArray(response)) {
            records = response;
        } else if (response && Array.isArray(response.data)) {
            records = response.data;
        }

        const Models: Record<string, any> = {};

        records.forEach((item: any) => {
            const modelName = item.model;
            if (!modelName) return;

            Models[modelName] = {
                service: item.service ?? null,
                route: item.route ?? null,
                default_field: item.default_field ?? null,
                visible_fields: Array.isArray(item.visible_fields) ? item.visible_fields : [],
                map_fields: item.map_fields && typeof item.map_fields === 'object' ? item.map_fields : {},
                show_at_general_report: item.show_at_general_report ?? false,
                show_at_dashboard: item.show_at_dashboard ?? false,
                show_at_notification: item.show_at_notification ?? false,
                relations: Array.isArray(item.relations) ? item.relations : [],
            };
        });

        return { Models };
    }

    /**
     * Reset/invalidate cache - forces fresh data on next request
     */
    static resetServiceMap() {
        this.serviceMap = null;
        this.cacheTimestamp = 0;
        this.ongoingRequest = null;
    }
    
    /**
     * Check if cached data is still valid
     */
    static isCacheValid(): boolean {
        return this.serviceMap && (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION;
    }
    
    /**
     * Get cached data directly without API call (returns null if cache invalid)
     */
    static getCachedData(): any {
        return this.isCacheValid() ? this.serviceMap : null;
    }
}